// Editorial content model (static marketing copy). No fabricated proof points.
import { MEDIA } from "./media";

export const NAV = {
  solutions: [
    { label: "Supply Chain AI", to: "/solutions/supply-chain-ai" },
    { label: "Marketing AI", to: "/solutions/marketing-ai" },
    { label: "Learning AI", to: "/solutions/learning-ai" },
    { label: "IT Transformation", to: "/solutions/it-transformation" },
    { label: "Data & Analytics", to: "/solutions/data-analytics" },
  ],
  practices: [
    { label: "Supply Chain AI", to: "/practices/supply-chain-ai" },
    { label: "Marketing AI", to: "/practices/marketing-ai" },
    { label: "Learning AI", to: "/practices/learning-ai" },
  ],
  firm: [
    { label: "Capabilities", to: "/capabilities" },
    { label: "Analytics", to: "/analytics" },
    { label: "Knowledge", to: "/knowledge" },
    { label: "Engagement Models", to: "/engagement-models" },
    { label: "Industries", to: "/industries" },
    { label: "Methodology", to: "/methodology" },
  ],
  company: [
    { label: "Insights", to: "/insights" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
  ],
};

export const PRACTICES = {
  "supply-chain-ai": {
    slug: "supply-chain-ai",
    name: "Supply Chain AI",
    number: "01",
    lead: "Turn planning, sourcing and fulfilment into decisions that improve every day.",
    accent: "#0F523A",
    vertical: "Supply Chain AI",
    intro:
      "We redesign the decisions that move goods — replenishment, allocation, routing and supplier collaboration — and apply AI where frequency, latency and cost of error justify it. The aim is not a dashboard; it is a supply chain that makes better calls, faster, inside your existing systems.",
    image:
      MEDIA.bannerSupplyChain,
    focus: [
      { title: "Demand and replenishment", text: "Forecasts and replenishment policies that adapt to signal, not to last quarter's assumptions." },
      { title: "Network and inventory", text: "Positioning inventory against service and working-capital targets your board actually holds you to." },
      { title: "Supplier collaboration", text: "Shared visibility and exception handling that shortens the loop between a problem and a decision." },
      { title: "Fulfilment and logistics", text: "Routing and allocation improvements measured in cost-to-serve and reliability, not in slideware." },
    ],
    outcomes: [
      "Fewer stock-outs and less expedited freight through better replenishment decisions.",
      "Working capital released by positioning inventory where it earns its keep.",
      "Planners freed from reconciliation to focus on the exceptions that matter.",
    ],
  },
  "marketing-ai": {
    slug: "marketing-ai",
    name: "Marketing AI",
    number: "02",
    lead: "Move marketing from output volume to decision quality, cycle time and cost to serve.",
    accent: "#D36135",
    vertical: "Marketing AI",
    intro:
      "We redesign how marketing decides — audience, offer, channel and creative — and apply AI to shorten the loop from insight to live campaign. Producing more content is easy and rarely the point; better decisions, made faster and measured against contribution, are.",
    image:
      MEDIA.bannerMarketing,
    focus: [
      { title: "Audience and segmentation", text: "Segments defined by behaviour and value, maintained continuously rather than rebuilt each campaign." },
      { title: "Offer and personalisation", text: "The right offer to the right customer, governed and measurable, not a black box." },
      { title: "Content operations", text: "Faster production with review and brand control kept firmly in human hands." },
      { title: "Measurement", text: "Metrics that reflect retention and margin, not vanity reach." },
    ],
    outcomes: [
      "Shorter cycle time from insight to campaign, so teams learn faster.",
      "Personalisation that lifts retention without eroding trust or brand.",
      "A clearer line between marketing decisions and contribution margin.",
    ],
  },
  "learning-ai": {
    slug: "learning-ai",
    name: "Learning AI",
    number: "03",
    lead: "Build capability at the pace transformation demands — in the flow of work.",
    accent: "#70304C",
    vertical: "Learning AI",
    intro:
      "We redesign how organisations build skills, applying AI to make learning relevant, timely and measurable. The goal is capability that shows up in the work — better decisions on the floor — not completion rates in a system.",
    image:
      MEDIA.bannerLearning,
    focus: [
      { title: "Capability mapping", text: "A clear view of the skills a transformed process actually requires." },
      { title: "In-the-flow support", text: "Guidance delivered where work happens, not parked in a separate portal." },
      { title: "Content and assessment", text: "Relevant material and honest assessment, produced and maintained efficiently." },
      { title: "Adoption", text: "Change management that treats learning as part of delivery, not an afterthought." },
    ],
    outcomes: [
      "Capability that keeps pace with process change instead of lagging behind it.",
      "Support delivered in the flow of work, reducing time-to-competence.",
      "Learning measured by behaviour on the job, not module completion.",
    ],
  },
};

export const DEEP_PAGES = {
  capabilities: {
    slug: "capabilities",
    name: "Capabilities",
    lead: "Consulting, implementation and applied AI — brought together to change how work is done.",
    intro:
      "ThinkStack is organised around a single idea: durable value comes from redesigning the process first, then applying technology where it compounds. Our capabilities span the full arc from diagnosis to a change that lives inside your operating reality.",
    sections: [
      { title: "Process diagnosis", text: "We map the chain of decisions inside a process with the people who run it, making constraints and cost visible before any technology is chosen." },
      { title: "Redesign", text: "We design the future-state process against the outcomes leaders are accountable for, within the constraints teams actually face." },
      { title: "Applied AI and data", text: "We apply intelligence where frequency, latency and cost of error justify it, and keep humans in the loop where errors are expensive." },
      { title: "Implementation", text: "We deliver change into systems of record and established ways of working — a business change with technology in service of it." },
      { title: "Adoption and capability", text: "We treat learning and change management as part of delivery, so the redesign holds after we leave." },
    ],
    related: [
      { label: "Methodology", to: "/methodology" },
      { label: "Engagement Models", to: "/engagement-models" },
      { label: "Analytics", to: "/analytics" },
    ],
  },
  analytics: {
    slug: "analytics",
    name: "Analytics",
    lead: "Analytics that inform decisions, not decorate reports.",
    intro:
      "We build analytics around the decisions they are meant to improve. The test of an analytics capability is not the number of dashboards; it is whether a specific decision is measurably better because of it.",
    sections: [
      { title: "Decision framing", text: "We start from the decision — who makes it, how often, and what a good outcome looks like — and work back to the data required." },
      { title: "Data foundations", text: "We assess the data that exists, its quality and its latency, and are candid about what is feasible today versus what needs investment." },
      { title: "Models in context", text: "We apply models where they change the economics of a decision, and prefer transparent approaches where accountability matters." },
      { title: "Measurement", text: "We instrument outcomes so the value of a change is observable, not assumed." },
    ],
    related: [
      { label: "Capabilities", to: "/capabilities" },
      { label: "Supply Chain AI", to: "/practices/supply-chain-ai" },
      { label: "Marketing AI", to: "/practices/marketing-ai" },
    ],
  },
  knowledge: {
    slug: "knowledge",
    name: "Knowledge",
    lead: "Turning scattered institutional knowledge into a dependable asset.",
    intro:
      "Enterprises hold enormous knowledge in documents, systems and people's heads. We help organisations make that knowledge findable, trustworthy and usable in the flow of work — with governance and boundaries defined before anything goes live.",
    sections: [
      { title: "Knowledge mapping", text: "We identify where critical knowledge lives, who relies on it, and where its absence slows decisions." },
      { title: "Structure and governance", text: "We design how knowledge is curated, versioned and trusted, so answers can be relied upon." },
      { title: "Access in the flow of work", text: "We deliver knowledge where work happens, respecting access controls and sensitivity." },
      { title: "Boundaries", text: "We define what a system should and should not answer before it is exposed to users." },
    ],
    related: [
      { label: "Learning AI", to: "/practices/learning-ai" },
      { label: "Methodology", to: "/methodology" },
      { label: "Capabilities", to: "/capabilities" },
    ],
  },
  "engagement-models": {
    slug: "engagement-models",
    name: "Engagement Models",
    lead: "Ways of working matched to where you are, not to how we prefer to bill.",
    intro:
      "Different problems need different engagements. We are explicit about the shape of the work up front — its scope, its measures of success and how a decision to continue is made.",
    sections: [
      { title: "Diagnostic", text: "A focused engagement to map a process, size the opportunity and produce a decision-ready redesign — before larger commitments." },
      { title: "Redesign and pilot", text: "A bounded build that proves the redesigned process and its supporting technology against agreed outcomes." },
      { title: "Delivery partnership", text: "A longer engagement to implement and scale change across a function, with capability transfer built in." },
      { title: "Advisory", text: "Ongoing counsel for leaders steering a transformation, without holding the pen on delivery." },
    ],
    related: [
      { label: "Methodology", to: "/methodology" },
      { label: "Capabilities", to: "/capabilities" },
      { label: "Industries", to: "/industries" },
    ],
  },
  industries: {
    slug: "industries",
    name: "Industries",
    lead: "Depth where the process is the business.",
    intro:
      "Our practices apply across sectors, but process redesign demands context. We focus where our supply chain, marketing and learning work meets real operating pressure. We describe our approach here rather than claim clients we have not been given permission to name.",
    sections: [
      { title: "Manufacturing and industrial", text: "Where planning, sourcing and fulfilment decisions determine service and margin." },
      { title: "Consumer and retail", text: "Where demand volatility and cost-to-serve reward better, faster decisions." },
      { title: "Logistics and distribution", text: "Where network and allocation decisions compound across thousands of daily choices." },
      { title: "Services and enterprise", text: "Where marketing and capability-building decisions drive growth and retention." },
    ],
    related: [
      { label: "Supply Chain AI", to: "/practices/supply-chain-ai" },
      { label: "Engagement Models", to: "/engagement-models" },
      { label: "Capabilities", to: "/capabilities" },
    ],
  },
  methodology: {
    slug: "methodology",
    name: "Methodology",
    lead: "Redesign the process. Apply AI where it compounds. Deliver into reality.",
    intro:
      "Our method is deliberately simple and hard to fake. It keeps the business outcome in front of the technology at every step.",
    sections: [
      { title: "01 — Frame the decision", text: "We identify the decisions inside a process and the outcomes leaders own, so effort is spent where it repays." },
      { title: "02 — Map the current process", text: "We map the real process with the people who run it, exposing constraints, latency and cost of error." },
      { title: "03 — Redesign", text: "We design the future state against outcomes and constraints — a plan that can actually be delivered." },
      { title: "04 — Apply intelligence", text: "We apply AI where frequency, latency and cost of error justify it, and keep humans in the loop where they must be." },
      { title: "05 — Deliver and adopt", text: "We implement into systems of record and build the capability that keeps the change in place." },
      { title: "06 — Measure", text: "We instrument outcomes so value is observable and the next decision is better than the last." },
    ],
    related: [
      { label: "Capabilities", to: "/capabilities" },
      { label: "Engagement Models", to: "/engagement-models" },
      { label: "Analytics", to: "/analytics" },
    ],
  },
};

export const REGIONS = [
  { name: "Singapore", note: "Headquarters", x: 300, y: 300 },
  { name: "Kuala Lumpur", note: "", x: 288, y: 278 },
  { name: "Jakarta", note: "", x: 312, y: 340 },
  { name: "Bangkok", note: "", x: 268, y: 232 },
  { name: "Ho Chi Minh City", note: "", x: 320, y: 250 },
  { name: "Manila", note: "", x: 372, y: 246 },
  { name: "Hong Kong", note: "", x: 352, y: 190 },
  { name: "Tokyo", note: "", x: 430, y: 150 },
  { name: "Mumbai", note: "", x: 150, y: 240 },
  { name: "Bengaluru", note: "", x: 168, y: 272 },
];
